import React, { useEffect, useState } from "react"
import { Card, Row, Col, Alert, Input, DatePicker, Button, Form, InputNumber, Collapse, notification } from "antd"
import { Typography } from "antd"
import FormInput from "components/FormInput"
import ParListSelect from "components/ParListSelect"
import InputCurrency from "components/InputCurrency"
import { getInvoiceReferenceByVin, type SupplierInvoiceResponse } from "services/fincontract"
import { type ReservationData, type ProposalResponse } from "services/proposal"
import { getThirdPartyAuthorizationByTpreference } from "services/thirdParty"
import { getOfferDetailByOfferId } from "services/pricing"
import moment from "moment"

const { Title, Text } = Typography

type WorkflowMode = 'initial' | 'reserved' | 'unreserved' | 'final' | 'spr'

type InvoiceFinalStepProps = {
  form: any
  workflowMode: WorkflowMode
  reservationData: ReservationData | null
  proposalResponse: ProposalResponse | null
  isCreditNote: boolean
  setIsCreditNote: (isCredit: boolean) => void
  originalInvoiceData: SupplierInvoiceResponse | null
  setOriginalInvoiceData: (data: SupplierInvoiceResponse | null) => void
  isInspect: boolean
  isLoadingInvoice: boolean
  setIsLoadingInvoice: (loading: boolean) => void
}

const InvoiceFinalStep: React.FC<InvoiceFinalStepProps> = ({
  form,
  workflowMode,
  reservationData,
  proposalResponse,
  isCreditNote,
  setIsCreditNote,
  originalInvoiceData,
  setOriginalInvoiceData,
  isInspect,
  isLoadingInvoice,
  setIsLoadingInvoice
}) => {
  const vinValue = Form.useWatch("VIN", form)
  const montantHTVehicule = Form.useWatch("montantHTVehicule", form)
  const montantTVA = Form.useWatch("montantTVA", form)
  const montantFrais = Form.useWatch("montantFrais", form)
  const montantAutresTaxes = Form.useWatch("montantAutresTaxes", form)

  const [vehicleCollapseOpen, setVehicleCollapseOpen] = useState(true)
  const [hasVehicleValidationErrors, setHasVehicleValidationErrors] = useState(false)

  const checkVehicleDetailsValidation = async () => {
    if (isCreditNote || workflowMode === 'spr') {
      return true
    }

    const vehicleFields = ['versionLabelLong', 'motorisation', 'versionYear', 'co2EmissionWLTP']
    const errors = []

    for (const field of vehicleFields) {
      const value = form.getFieldValue(field)
      if (!value || (typeof value === 'string' && value.trim() === '')) {
        errors.push(field)
      }
    }

    const hasErrors = errors.length > 0
    setHasVehicleValidationErrors(hasErrors)

    if (hasErrors) {
      setVehicleCollapseOpen(true)

      try {
        await form.validateFields(vehicleFields)
      } catch (validationError) {
      }

      notification.warning({
        message: "Champs obligatoires manquants",
        description: "Veuillez remplir tous les détails véhicule obligatoires dans la section ci-dessous.",
        duration: 5,
      })

      return false
    }

    return true
  }

  React.useImperativeHandle(form, () => ({
    ...form,
    validateVehicleDetails: checkVehicleDetailsValidation
  }))

const disabledDate = (current: moment.Moment) => {
  if (!current) return false

  const thirtyDaysFromToday = moment().add(30, 'days').endOf('day')
  
  return current.isAfter(thirtyDaysFromToday)
}

const getValidEmissionDate = (requestedDate?: moment.Moment) => {
  const thirtyDaysFromToday = moment().add(30, 'days').endOf('day')
  const dateToCheck = requestedDate || moment()

  if (dateToCheck.isBefore(thirtyDaysFromToday) || dateToCheck.isSame(thirtyDaysFromToday, 'day')) {
    return dateToCheck
  }

  return moment()
}


  const fetchFranchiseDuration = async (offerId: string | number): Promise<number> => {
    try {
      if (offerId) {
        const numericOfferId = typeof offerId === 'string' ? parseInt(offerId, 10) : offerId

        if (isNaN(numericOfferId)) {
          console.warn("Invalid offerId:", offerId)
          return 0
        }

        const franchiseResponse = await getOfferDetailByOfferId({
          offerid: numericOfferId
        })

        if (franchiseResponse.code === "200" && franchiseResponse.data?.partnbdaysmax) {
          return franchiseResponse.data.partnbdaysmax
        }
      }

      return 0
    } catch (error) {
      console.error("Error fetching franchise duration:", error)
      return 0
    }
  }

  const searchInvoiceByVin = async (vin: string) => {
    if (!isCreditNote) {
      return null
    }

    try {
      setIsLoadingInvoice(true)

      const response = await getInvoiceReferenceByVin({ vin: vin.trim() })

      if (response.code === "200" && response.data) {
        const invoiceData: SupplierInvoiceResponse = response.data
        setOriginalInvoiceData(invoiceData)

        let calculatedHTVehicule = 0
        let calculatedTVA = 0
        let calculatedFrais = 0
        let calculatedAutresTaxes = 0

        if (invoiceData.suppInvoiceLines && invoiceData.suppInvoiceLines.length > 0) {
          invoiceData.suppInvoiceLines.forEach(line => {
            if (line.siltype === "VEHICLE" || line.siltype === "ASSET") {
              calculatedHTVehicule += line.silnettotalamount || 0
              calculatedTVA += line.siltaxtotalamount || 0
            } else if (line.siltype === "FEES" || line.siltype === "FEE") {
              calculatedFrais += line.silgrosstotalamount || 0
            } else if (line.siltype === "OTHER" || line.siltype === "TAX") {
              calculatedAutresTaxes += line.silgrosstotalamount || 0
            }
          })
        } else {
          calculatedHTVehicule = invoiceData.sinettotal || 0
          calculatedTVA = invoiceData.sitaxtotal || 0
        }

        const calculatedTTC = calculatedHTVehicule + calculatedTVA + calculatedFrais + calculatedAutresTaxes

        form.setFieldsValue({
          numFactureOrigine: invoiceData.sidocreference,
          codeClientFacture: invoiceData.tprefclient,
          montantHTVehicule: -Math.abs(calculatedHTVehicule),
          montantTVA: -Math.abs(calculatedTVA),
          montantTTC: -Math.abs(calculatedTTC),
          montantFrais: -Math.abs(calculatedFrais),
          montantAutresTaxes: -Math.abs(calculatedAutresTaxes),
          codeDevise: invoiceData.currcode || "EUR",
          VIN: vin,
          dateEmissionFacture: getValidEmissionDate(),
          dateDebutCalculInteret: getValidEmissionDate(),
          dureeFranchise: 0,
          codePays: "FR",
          typeVehicule: "1"
        })

        notification.success({
          message: "Facture d'origine détectée",
          description: `Facture ${invoiceData.sidocreference} trouvée - montants en négatif pour l'avoir (${invoiceData.suppInvoiceLines?.length || 0} lignes)`,
          duration: 5,
        })

        return invoiceData
      } else {
        throw new Error("Facture d'origine non trouvée pour ce VIN")
      }
    } catch (error: any) {
      notification.error({
        message: "Erreur",
        description: error.message || "Impossible de trouver la facture d'origine avec ce VIN",
        duration: 5,
      })
      return null
    } finally {
      setIsLoadingInvoice(false)
    }
  }

  const handleCreditNoteToggle = async (isCredit: boolean) => {
    setIsCreditNote(isCredit)

    if (isCredit) {
      form.setFieldValue("codeMovement", "CRNO")
      form.setFieldValue("typeDocument", "04")

      form.setFieldsValue({
        montantHTVehicule: 0,
        montantTVA: 0,
        montantTTC: 0,
        montantFrais: 0,
        montantAutresTaxes: 0,
        numFactureOrigine: ""
      })

      const currentVin = form.getFieldValue("VIN")
      if (currentVin && currentVin.length === 17) {
        await searchInvoiceByVin(currentVin)
      }
    } else {
      form.setFieldValue("codeMovement", "INVO")
      form.setFieldValue("typeDocument", "01")
      setOriginalInvoiceData(null)

      if (workflowMode === 'reserved' && reservationData) {
        const data = reservationData as any
        form.setFieldsValue({
          montantHTVehicule: data.ctfinancedamount || data.reservationAmount || 0,
          montantTTC: data.ctfinancedamount || data.reservationAmount || 0,
          montantTVA: 0,
          montantFrais: 0,
          montantAutresTaxes: 0,
          numFactureOrigine: ""
        })
      } else {
        const currentHT = form.getFieldValue("montantHTVehicule")
        const currentTVA = form.getFieldValue("montantTVA")
        const currentTTC = form.getFieldValue("montantTTC")
        const currentFrais = form.getFieldValue("montantFrais")
        const currentAutres = form.getFieldValue("montantAutresTaxes")

        if (currentHT < 0) form.setFieldValue("montantHTVehicule", Math.abs(currentHT))
        if (currentTVA < 0) form.setFieldValue("montantTVA", Math.abs(currentTVA))
        if (currentTTC < 0) form.setFieldValue("montantTTC", Math.abs(currentTTC))
        if (currentFrais < 0) form.setFieldValue("montantFrais", Math.abs(currentFrais))
        if (currentAutres < 0) form.setFieldValue("montantAutresTaxes", Math.abs(currentAutres))

        form.setFieldValue("numFactureOrigine", "")
      }
    }
  }

  const isFieldDisabledForCreditNote = (fieldName: string): boolean => {
    if (isInspect) return true

    if (fieldName === 'montantTTC') return true

    if (isCreditNote && originalInvoiceData) {
      const autoFilledFields = [
        'montantHTVehicule', 'montantTVA', 'montantTTC',
        'codeClientFacture', 'codeDevise', 'VIN', 'numFactureOrigine',
        'montantFrais', 'montantAutresTaxes'
      ]
      return autoFilledFields.includes(fieldName)
    }

    if (workflowMode === 'reserved' && reservationData) {
      const reservationFields = [
        'codeClientFacture', 'VIN', 'codeDevise', 'codeMarque',
        'familleFinancement'
      ]
      return reservationFields.includes(fieldName)
    }

    if (workflowMode === 'unreserved' && proposalResponse) {
      const preFilledFields = [
        'familleFinancement',
        'codeClientFacture',
        'VIN',
        'codeDevise',
        'codePays',
        'typeVehicule',
        'dateEmissionFacture',
        'dateDebutCalculInteret'
      ]
      return preFilledFields.includes(fieldName)
    }

    if (workflowMode === 'spr') {
      return false
    }

    return false
  }

  const getFieldStyle = (fieldName: string) => {
    const isDisabled = isFieldDisabledForCreditNote(fieldName)
    return {
      backgroundColor: isDisabled ? '#f5f5f5' : undefined,
      cursor: isDisabled ? 'not-allowed' : undefined,
      color: isDisabled ? '#666' : undefined
    }
  }

  const renderCreditNoteToggle = () => (
    <Row gutter={[24, 16]} style={{ marginBottom: 24 }}>
      <Col span={24}>
        <div style={{ textAlign: 'center', background: '#f5f5f5', padding: '16px', borderRadius: '8px' }}>
          <Text strong style={{ marginRight: '16px' }}>Type de document :</Text>
          <Button.Group>
            <Button
              type={!isCreditNote ? "primary" : "default"}
              onClick={() => handleCreditNoteToggle(false)}
            >
              Facture
            </Button>
            <Button
              type={isCreditNote ? "primary" : "default"}
              onClick={() => handleCreditNoteToggle(true)}
            >
              Avoir
            </Button>
          </Button.Group>
        </div>
      </Col>
    </Row>
  )

  useEffect(() => {
    if (isCreditNote && vinValue && vinValue.length === 17) {
      const timeoutId = setTimeout(() => {
        searchInvoiceByVin(vinValue)
      }, 800)

      return () => clearTimeout(timeoutId)
    }
  }, [vinValue, isCreditNote])

  useEffect(() => {
    const htValue = Number.parseFloat(montantHTVehicule) || 0
    const tvaValue = Number.parseFloat(montantTVA) || 0
    const fraisValue = Number.parseFloat(montantFrais) || 0
    const autresTaxesValue = Number.parseFloat(montantAutresTaxes) || 0

    const ttcValue = htValue + tvaValue + fraisValue + autresTaxesValue
    form.setFieldValue("montantTTC", Number.parseFloat(ttcValue.toFixed(2)))
  }, [montantHTVehicule, montantTVA, montantFrais, montantAutresTaxes, form])

  useEffect(() => {
    const setupFormData = async () => {
      if (workflowMode === 'reserved' && reservationData) {
        const data = reservationData as any

        const fullVin = data.vin1 && data.vin2
          ? `${data.vin1}${data.vin2}`
          : data.vin || data.vin1 || ''

        const franchiseDuration = await fetchFranchiseDuration(data.offerid)

        const commonData: Record<string, any> = {
          codeClientFacture: data.clientreference || data.clientCode || '',
          familleFinancement: data.prcode || data.productFamily || '',
          familleFinancementLabel: form.getFieldValue('familyLabel') || data.prcode || data.productFamily || '',
          VIN: fullVin,
          numFacture: "",
          dateEmissionFacture: getValidEmissionDate(),
          dateDebutCalculInteret: getValidEmissionDate(),
          codePays: "FR",
          codeDevise: data.currcode || "EUR",
          typeVehicule: "1",
        }

        const currentBrand = form.getFieldValue("codeMarque")
        if (currentBrand) {
          commonData.codeMarque = currentBrand
          commonData.brandLabel = currentBrand
        }

        if (!isCreditNote) {
          commonData.montantHTVehicule = data.ctfinancedamount || data.reservationAmount || 0
          commonData.montantTTC = data.ctfinancedamount || data.reservationAmount || 0
          commonData.montantTVA = 0
          commonData.montantFrais = 0
          commonData.montantAutresTaxes = 0
        }

        form.setFieldsValue(commonData)
      }

      else if (workflowMode === 'unreserved' && proposalResponse) {
        const data = proposalResponse as any

        const offerId = data.offerid || data.offerId || data.ctofferid
        const franchiseDuration = await fetchFranchiseDuration(offerId)
        const fullVin = data.vin1 && data.vin2
          ? `${data.vin1}${data.vin2}`
          : data.vin || data.vin1 || ''
        const unreservedData = {
          dureeFranchise: '',
          familleFinancement: data.prcode || data.productFamily || '',
          familleFinancementLabel: form.getFieldValue('familyLabel') || data.prcode || data.productFamily || '',
          codeClientFacture: data.clientreference || data.clientCode || '',
          VIN: fullVin,
          codeDevise: data.currcode || "EUR",
          codePays: "FR",
          typeVehicule: "1",
          dateEmissionFacture: getValidEmissionDate(),
          dateDebutCalculInteret: getValidEmissionDate(),
        }

        form.setFieldsValue(unreservedData)
      }

      else if ((workflowMode === 'initial' || workflowMode === 'final') && (reservationData || proposalResponse)) {
        const data = (reservationData || proposalResponse) as any
        const offerId = data?.offerid || data?.offerId || data?.ctofferid

        if (offerId) {
          const franchiseDuration = await fetchFranchiseDuration(offerId)
          form.setFieldValue('dureeFranchise', franchiseDuration)
        }
      }
      else if (workflowMode === 'spr') {
        const sprData = {
          dateEmissionFacture: getValidEmissionDate(),
          codePays: "FR",
        }

        form.setFieldsValue(sprData)
      }
    }

    setupFormData()
  }, [workflowMode, reservationData, proposalResponse, isCreditNote, form])

  useEffect(() => {
    const handleCreditModeForPrefin = async () => {
      if (workflowMode === 'reserved' && reservationData && !isCreditNote) {
        const data = reservationData as any
        const reservationStatus = data.ctstatus || data.status || 'UNKNOWN'

        if (reservationStatus.toUpperCase() === 'PREFIN' || reservationStatus.toUpperCase() === 'INVOICED') {

          const fullVin = data.vin1 && data.vin2
            ? `${data.vin1}${data.vin2}`
            : data.vin || data.vin1 || ''

          const clientRef = data.clientreference || data.clientCode
          const prcode = data.prcode || data.productFamily

          if (clientRef && prcode) {
            try {
              const basicResponse = await getThirdPartyAuthorizationByTpreference({
                tpreference: clientRef,
                prcode: prcode,
              })

              if (basicResponse.code === "200" && basicResponse.data?.authbrand) {
                const detectedBrand = basicResponse.data.authbrand
                console.log("🏷️ [FINAL STEP] Brand detected for credit note:", detectedBrand)

                form.setFieldsValue({
                  codeMarque: detectedBrand,
                  brandLabel: detectedBrand
                })
              }
            } catch (error) {
              console.warn(" [FINAL STEP] Error searching brand:", error)
            }
          }

          setIsCreditNote(true)
          form.setFieldValue("codeMovement", "CRNO")
          form.setFieldValue("typeDocument", "04")

          if (fullVin && fullVin.length === 17) {
            try {
              setIsLoadingInvoice(true)

              const response = await getInvoiceReferenceByVin({ vin: fullVin.trim() })

              if (response.code === "200" && response.data) {
                const invoiceData: SupplierInvoiceResponse = response.data
                setOriginalInvoiceData(invoiceData)

                let calculatedHTVehicule = 0
                let calculatedTVA = 0
                let calculatedFrais = 0
                let calculatedAutresTaxes = 0

                if (invoiceData.suppInvoiceLines && invoiceData.suppInvoiceLines.length > 0) {
                  invoiceData.suppInvoiceLines.forEach(line => {
                    if (line.siltype === "VEHICLE" || line.siltype === "ASSET") {
                      calculatedHTVehicule += line.silnettotalamount || 0
                      calculatedTVA += line.siltaxtotalamount || 0
                    } else if (line.siltype === "FEES" || line.siltype === "FEE") {
                      calculatedFrais += line.silgrosstotalamount || 0
                    } else if (line.siltype === "OTHER" || line.siltype === "TAX") {
                      calculatedAutresTaxes += line.silgrosstotalamount || 0
                    }
                  })
                } else {
                  calculatedHTVehicule = invoiceData.sinettotal || 0
                  calculatedTVA = invoiceData.sitaxtotal || 0
                }

                const calculatedTTC = calculatedHTVehicule + calculatedTVA + calculatedFrais + calculatedAutresTaxes

                form.setFieldsValue({
                  numFactureOrigine: invoiceData.sidocreference,
                  montantHTVehicule: -Math.abs(calculatedHTVehicule),
                  montantTVA: -Math.abs(calculatedTVA),
                  montantTTC: -Math.abs(calculatedTTC),
                  montantFrais: -Math.abs(calculatedFrais),
                  montantAutresTaxes: -Math.abs(calculatedAutresTaxes)
                })

                notification.success({
                  message: "Facture d'origine détectée automatiquement",
                  description: `Facture ${invoiceData.sidocreference} trouvée - montants en négatif pour l'avoir`,
                  duration: 5,
                })

              } else {
                console.warn(" [FINAL STEP] Original invoice not found for VIN:", fullVin)
              }
            } catch (error) {
              console.error(" [FINAL STEP] Error loading original invoice:", error)
            } finally {
              setIsLoadingInvoice(false)
            }
          }
        }
      }
    }

    handleCreditModeForPrefin()
  }, [workflowMode, reservationData, isCreditNote])

  return (
    <Card title="Facture finale" className="wizard-step-card">
      {renderCreditNoteToggle()}

      <Row gutter={[24, 16]}>
        <Col span={8}>
          <Form.Item
            name="codeClientFacture"
            label="Code Client"
            rules={[{ required: true }]}
          >
            <Input
              disabled={isFieldDisabledForCreditNote('codeClientFacture')}
              style={getFieldStyle('codeClientFacture')}
            />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            name="codeMarque"
            label="Marque"
            rules={[{ required: true }]}
          >
            {workflowMode === 'spr' ? (
              <ParListSelect
                parcode="TPBRAND"
                disabled={isInspect}
                placeholder="Sélectionnez une marque"
                allowClear
                showSearch
                filterOption={(input: string, option: any) =>
                  (option?.children?.toString() || '').toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
                onSelect={(value: string, option: any) => {
                  form.setFieldValue("codeMarque", value)
                  form.setFieldValue("brandLabel", option?.children || value)
                }}
              />
            ) : (
              <Input
                disabled={isFieldDisabledForCreditNote('codeMarque')}
                style={getFieldStyle('codeMarque')}
              />
            )}
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            label="Famille Produit"
            rules={[{ required: true }]}
          >
            <Input
              disabled={isFieldDisabledForCreditNote('familleFinancement')}
              style={getFieldStyle('familleFinancement')}
              value={form.getFieldValue('familyLabel') || form.getFieldValue('familleFinancement')}
              readOnly
            />
            <Form.Item name="familleFinancement" hidden>
              <Input />
            </Form.Item>
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={[24, 16]}>
        {workflowMode !== 'spr' && (
          <Col span={isCreditNote ? 8 : 12}>
            <Form.Item
              name="VIN"
              label="VIN"
              rules={[
                { required: true, message: "Le VIN est obligatoire" },
                { len: 17, message: "Le VIN doit contenir 17 caractères" }
              ]}
            >
              <Input
                placeholder="VIN (17 caractères)"
                maxLength={17}
                disabled={isFieldDisabledForCreditNote('VIN')}
                style={getFieldStyle('VIN')}
              />
            </Form.Item>
          </Col>
        )}
        {isCreditNote && (
          <Col span={8}>
            <Form.Item
              name="numFactureOrigine"
              label="Numéro facture d'origine"
              rules={[{ required: isCreditNote, message: "Facture d'origine obligatoire pour un avoir" }]}
            >
              <Input
                placeholder="Auto-détecté depuis le VIN"
                disabled={isFieldDisabledForCreditNote('numFactureOrigine')}
                style={getFieldStyle('numFactureOrigine')}
              />
            </Form.Item>
          </Col>
        )}
        <Col span={workflowMode === 'spr' ? 12 : 8}>
          <Form.Item
            name="numFacture"
            label={isCreditNote ? "Numéro d'avoir" : "Numéro de facture"}
            rules={[{ required: true, message: `Veuillez saisir le numéro ${isCreditNote ? "d'avoir" : "de facture"}` }]}
          >
            <Input
              placeholder={isCreditNote ? "Numéro d'avoir à créer" : "Numéro de facture"}
              disabled={isInspect}
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={[24, 16]}>
        <Col span={12}>
          <Form.Item
            name="dateEmissionFacture"
            label="Date d'émission"
            rules={[{ required: true, message: "Veuillez sélectionner la date d'émission" }]}
          >
            <DatePicker
              placeholder="Date d'émission"
              className="w-100"
              disabled={isInspect}
              disabledDate={disabledDate}
              showToday
              allowClear={false}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name="codePays"
            label="Pays"
            rules={[{ required: true, message: "Veuillez sélectionner le pays" }]}
          >
            <ParListSelect
              parcode="COUNTRY"
              disabled={isInspect}
            />
          </Form.Item>
        </Col>
      </Row>
      <Title level={5}>Montants</Title>

      <Row gutter={[24, 16]}>
        <Col span={8}>
          <Form.Item
            name="montantHTVehicule"
            label="Montant HT Véhicule"
            rules={[{ required: true, message: "Veuillez saisir le montant HT" }]}
          >
            <InputCurrency
              placeholder="Montant HT"
              min={isCreditNote ? undefined : 0}
              disabled={isFieldDisabledForCreditNote('montantHTVehicule')}
              style={{
                backgroundColor: isFieldDisabledForCreditNote('montantHTVehicule') ? '#f5f5f5' : undefined
              }}
            />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            name="montantTVA"
            label="Montant TVA"
          >
            <InputCurrency
              placeholder="Montant TVA"
              min={isCreditNote ? undefined : 0}
              disabled={isFieldDisabledForCreditNote('montantTVA')}
              style={{
                backgroundColor: isFieldDisabledForCreditNote('montantTVA') ? '#f5f5f5' : undefined
              }}
            />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            name="montantTTC"
            label="Montant TTC"
            rules={[{ required: true, message: "Veuillez saisir le montant TTC" }]}
          >
            <InputCurrency
              placeholder="Montant TTC"
              min={isCreditNote ? undefined : 0}
              disabled={isFieldDisabledForCreditNote('montantTTC')}
              style={{
                backgroundColor: isFieldDisabledForCreditNote('montantTTC') ? '#f5f5f5' : undefined
              }}
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={[24, 16]}>
        <Col span={8}>
          <Form.Item
            name="montantFrais"
            label="Montant des Frais"
          >
            <InputCurrency
              placeholder="Frais"
              min={isCreditNote ? undefined : 0}
              disabled={isFieldDisabledForCreditNote('montantFrais')}
              style={{
                backgroundColor: isFieldDisabledForCreditNote('montantFrais') ? '#f5f5f5' : undefined
              }}
            />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            name="montantAutresTaxes"
            label="Autres Taxes"
          >
            <InputCurrency
              placeholder="Autres taxes"
              min={isCreditNote ? undefined : 0}
              disabled={isFieldDisabledForCreditNote('montantAutresTaxes')}
              style={{
                backgroundColor: isFieldDisabledForCreditNote('montantAutresTaxes') ? '#f5f5f5' : undefined
              }}
            />
          </Form.Item>
        </Col>
        {!isCreditNote && (
          <Col span={8}>
            <Form.Item
              name="dureeFranchise"
              label="Durée Franchise (jours)"
              rules={[{ required: true, message: "Veuillez saisir la durée de franchise" }]}
            >
              <InputNumber
                placeholder="Jours"
                min={0}
                className="w-100"
                disabled={isInspect}
              />
            </Form.Item>
          </Col>
        )}
      </Row>

      {!isCreditNote && workflowMode !== 'spr' && (
        <>
          {hasVehicleValidationErrors && (
            <Alert
              message="Attention"
              description="Les détails véhicule ci-dessous sont obligatoires pour continuer."
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}
          <Collapse
            ghost
            activeKey={vehicleCollapseOpen ? ['vehicle-details'] : []}
            onChange={(keys) => setVehicleCollapseOpen(keys.length > 0)}
            items={[
              {
                key: 'vehicle-details',
                label: (
                  <Title level={5} style={{ margin: 0, color: hasVehicleValidationErrors ? '#ff4d4f' : undefined }}>
                    Détails véhicule et techniques <Text type="secondary"></Text>
                    {hasVehicleValidationErrors && <Text type="danger"> *</Text>}
                  </Title>
                ),
                children: (
                  <>
                    <Row gutter={[24, 16]}>
                      <Col span={12}>
                        <Form.Item
                          name="versionLabelLong"
                          label="Version"
                          rules={[{ required: true, message: "Veuillez saisir la version" }]}
                        >
                          <Input placeholder="Version du véhicule" disabled={isInspect} />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          name="motorisation"
                          label="Motorisation"
                          rules={[{ required: true, message: "Veuillez sélectionner la motorisation" }]}
                        >
                          <ParListSelect parcode="ENGTYPE" disabled={isInspect} />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Row gutter={[24, 16]}>
                      <Col span={8}>
                        <Form.Item
                          name="versionYear"
                          label="Année Version"
                          rules={[{ required: true, message: "Veuillez saisir l'année" }]}
                        >
                          <InputNumber
                            placeholder="Année"
                            min={1900}
                            max={2100}
                            className="w-100"
                            disabled={isInspect}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item
                          name="co2EmissionWLTP"
                          label="CO2 Emission WLTP"
                          rules={[{ required: true, message: "Veuillez saisir l'émission CO2" }]}
                        >
                          <InputNumber
                            placeholder="CO2 g/km"
                            min={0}
                            step={0.1}
                            className="w-100"
                            disabled={isInspect}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item
                          name="blvd"
                          label="BLVD"
                        >
                          <Input
                            placeholder="BLVD (16 caractères)"
                            maxLength={16}
                            disabled={isInspect}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  </>
                )
              }
            ]}
          />
        </>
      )}
    </Card>
  )
}

export default InvoiceFinalStep